import {
  Alert,
  Box,
  Button,
  Heading,
  Icon,
  Modal,
  Paragraph,
  PropertyCard,
  Text,
  TextButton,
  Upsell,
} from '@zillow/constellation-10';
import {
  IconDollarSignCircleFilled,
  IconHouseUserFilled,
  IconProfileFilled,
  IconSignatureFilled,
  IconYardSignFilled,
} from '@zillow/constellation-icons';

import { LoadingButton, LoadingParagraph, LoadingText, shimmerStyles } from '@zg-rentals/loading-components';
import {
  useRentalManagerAccountViewZrmPropertiesWebApiHandler_added,
  useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { formatAddress, getPhotoThumbUrl, getPropertyTypeLabel, pluralizeWord } from '../../../utils';

import { ErrorMessageConstants } from '../../../constants';

import {
  emitAddLeaseEvent,
  emitAddTenantEvent,
  emitInviteToApplyEvent,
  emitListPropertyForRentEvent,
  emitSetUpPaymentsEvent,
} from '../../../analytics/events/propertyAddedNextSteps';

import type { ReactNode } from 'react';
import type { RentalManagerAuthCheckUserInfoWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { AddPropertyDetailsState } from './AddPropertyDetailsModal';

// The backend can omit an action's url; without one this stays a button so the click is an
// analytics-only no-op rather than a dead link to the current page.
type ActionCTAProps = {
  url?: string;
  onClick: () => void;
  children: string;
  filled?: boolean;
};

type ActionControlProps = {
  filled: boolean;
  asChild?: boolean;
  onClick?: () => void;
  children: ReactNode;
};

const ActionControl = ({ filled, ...props }: ActionControlProps) =>
  filled ? <Button emphasis="filled" {...props} /> : <TextButton {...props} />;

const ActionCTA = ({ url, onClick, children, filled = false }: ActionCTAProps) =>
  url ? (
    <ActionControl filled={filled} asChild>
      <a href={url} onClick={onClick}>
        {children}
      </a>
    </ActionControl>
  ) : (
    <ActionControl filled={filled} onClick={onClick}>
      {children}
    </ActionControl>
  );

const UpsellSkeleton = () => (
  <Box
    css={{
      display: 'flex',
      gap: '300',
      padding: '400',
      border: '1px solid token(colors.border.muted)',
      borderRadius: '300',
    }}
  >
    <LoadingText isLoading={true} textStyle="heading-lg" loadingWidth="24px">
      &nbsp;
    </LoadingText>
    <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
      <LoadingText isLoading={true} textStyle="heading-xs" loadingWidth="55%">
        &nbsp;
      </LoadingText>
      <LoadingParagraph isLoading={true} textStyle="body-sm" loadingWidth="90%">
        &nbsp;
      </LoadingParagraph>
      <LoadingButton isLoading={true} loadingWidth="80px">
        &nbsp;
      </LoadingButton>
    </Box>
  </Box>
);

type Props = AddPropertyDetailsState & {
  isOpen: boolean;
  onClose: () => void;
  multiplexBuildingSessionId?: string;
  firstUnitListingAlias?: string;
  onAddTenantClick: (listingAlias: string) => void;
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
};

const PropertyAddedSuccessStep = ({
  isOpen,
  listingAlias,
  isStreetEasyRestricted,
  multiplexBuildingSessionId,
  firstUnitListingAlias,
  onClose,
  onAddTenantClick,
  user,
}: Props): JSX.Element => {
  const effectiveListingAlias = firstUnitListingAlias || listingAlias;

  const { data: webAnalyticsData } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    { listingAlias: effectiveListingAlias!, includeLeases: false },
    { enabled: isOpen && Boolean(effectiveListingAlias) },
  );

  const { data: addedData } = useRentalManagerAccountViewZrmPropertiesWebApiHandler_added(
    { listingAlias: effectiveListingAlias!, multiplexBuildingSessionId },
    {
      enabled: isOpen && Boolean(effectiveListingAlias),
      meta: {
        tags: ['property-added'],
        context: { api: 'property-added', details: 'account view property added success actions' },
      },
    },
  );

  const { actions, address, photoKey, propertyType, numUnits } = addedData ?? {};

  const { formattedStreet, formattedCityStateZip } = address
    ? formatAddress(address)
    : { formattedStreet: undefined, formattedCityStateZip: undefined };
  const formattedAddress = [formattedStreet, formattedCityStateZip].filter(Boolean).join(', ');

  const displayPropertyType = getPropertyTypeLabel({ type: addedData?.type, propertyType }) ?? '';
  const propertyTypeLine =
    numUnits && numUnits > 1
      ? `${displayPropertyType} • ${numUnits} ${pluralizeWord(numUnits, 'unit', 'units')}`
      : displayPropertyType;

  const makeActionHandler =
    (
      emitFn: (args: { user: NonNullable<typeof user>; currentProperty: NonNullable<typeof webAnalyticsData> }) => void,
    ) =>
    () => {
      if (user && webAnalyticsData) {
        emitFn({ user, currentProperty: webAnalyticsData });
      }
    };

  const handleCreateListingClick = makeActionHandler(emitListPropertyForRentEvent);
  const handleInviteToApplyClick = makeActionHandler(emitInviteToApplyEvent);
  const handleAddLeaseClick = makeActionHandler(emitAddLeaseEvent);
  const handleSetUpPaymentsClick = makeActionHandler(emitSetUpPaymentsEvent);
  const handleAddTenantClick = () => {
    if (user && webAnalyticsData) {
      emitAddTenantEvent({ user, currentProperty: webAnalyticsData });
    }
    onAddTenantClick(effectiveListingAlias ?? '');
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'lg' }}
      dividers={false}
      header={<Heading level={2}>Your property was added</Heading>}
      body={
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: '800',
            paddingBlockEnd: '400',
            paddingInline: { base: '0', md: '400' },
          }}
        >
          {!addedData ? (
            <Box css={{ display: 'flex', gap: '400', alignItems: 'center' }}>
              <Box
                data-loading={true}
                css={{
                  // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 150px` falls between `3600` (144px) and `4000` (160px) on the numeric sizes scale
                  width: '150px',
                  height: '2500',
                  borderRadius: 'obj.soft',
                  flexShrink: '0',
                  ...shimmerStyles,
                }}
              />
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '100', flex: '1' }}>
                <LoadingText isLoading={true} textStyle="body-lg-bold" loadingWidth="60%">
                  &nbsp;
                </LoadingText>
                <LoadingText isLoading={true} textStyle="body-sm" loadingWidth="120px">
                  &nbsp;
                </LoadingText>
              </Box>
            </Box>
          ) : (
            <Box css={{ display: 'flex', gap: '400', alignItems: 'center' }}>
              <Box
                data-testid="success-property-image"
                css={{
                  // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 150px` falls between `3600` (144px) and `4000` (160px) on the numeric sizes scale
                  width: '150px',
                  height: '2500',
                  borderRadius: 'obj.soft',
                  overflow: 'hidden',
                  flexShrink: '0',
                }}
              >
                <PropertyCard.Photo
                  src={getPhotoThumbUrl(photoKey)}
                  alt={formattedAddress}
                  fallbackImg={<PropertyCard.PhotoFallback />}
                  css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', gap: '100' }}>
                <Text textStyle="body-lg-bold" css={{ display: 'block' }} data-testid="success-address">
                  {formattedAddress}
                </Text>
                <Text textStyle="body-sm" css={{ display: 'block' }} data-testid="success-property-type">
                  {propertyTypeLine}
                </Text>
              </Box>
            </Box>
          )}

          <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
            <Text textStyle="body-bold">What would you like to do next?</Text>

            {!addedData ? (
              <>
                <UpsellSkeleton />
                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
                    gap: '400',
                  }}
                >
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                  <UpsellSkeleton />
                </Box>
              </>
            ) : (
              <>
                {(actions?.listing.isAllowed || isStreetEasyRestricted) && (
                  <Upsell
                    data-testid="success-action-list-property"
                    elevated={false}
                    closeable={false}
                    media={
                      <Box
                        css={{
                          display: 'inline-flex',
                          alignSelf: 'flex-start',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'bg.accent.blue.impact',
                          borderRadius: 'obj.default',
                          padding: 'tight',
                        }}
                      >
                        <Icon
                          render={<IconYardSignFilled />}
                          size="md"
                          css={{
                            color: 'icon.onImpact.action.hero.default',
                            '& rect': { fill: 'icon.onImpact.express.trust.hero' },
                          }}
                        />
                      </Box>
                    }
                    header={
                      <Heading level={3} textStyle="heading-xs">
                        {isStreetEasyRestricted
                          ? 'Manage your property'
                          : multiplexBuildingSessionId
                            ? 'List property or units for rent'
                            : 'List property for rent'}
                      </Heading>
                    }
                    body={
                      isStreetEasyRestricted ? (
                        <Alert>{ErrorMessageConstants.LOCATION_RESTRICTION.NYC}</Alert>
                      ) : (
                        <Paragraph>
                          1 million visitors come to Zillow, Trulia, and HotPads every day — show up where they’re
                          looking.
                        </Paragraph>
                      )
                    }
                    action={
                      isStreetEasyRestricted ? (
                        <TextButton asChild>
                          <a href={`/rental-manager/properties/${effectiveListingAlias}/dashboard/overview`}>
                            Go to dashboard
                          </a>
                        </TextButton>
                      ) : (
                        <ActionCTA url={actions?.listing.url} onClick={handleCreateListingClick} filled>
                          Create listing
                        </ActionCTA>
                      )
                    }
                  />
                )}

                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' },
                    gap: '400',
                  }}
                >
                  {actions?.tenants.isAllowed && (
                    <Upsell
                      data-testid="success-action-add-tenant"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconHouseUserFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Add a tenant
                        </Heading>
                      }
                      body={
                        <Paragraph>Keep track of your residents, their lease terms, and contact details.</Paragraph>
                      }
                      action={<TextButton onClick={handleAddTenantClick}>Add a tenant</TextButton>}
                    />
                  )}

                  {actions?.applications.isAllowed && (
                    <Upsell
                      data-testid="success-action-invite-to-apply"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconProfileFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Invite to apply
                        </Heading>
                      }
                      body={<Paragraph>Screen renters with Zillow&apos;s hassle-free applications.</Paragraph>}
                      action={
                        <ActionCTA url={actions.applications.url} onClick={handleInviteToApplyClick}>
                          Invite to apply
                        </ActionCTA>
                      }
                    />
                  )}

                  {actions?.leases.isAllowed && (
                    <Upsell
                      data-testid="success-action-add-lease"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconSignatureFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Add a lease
                        </Heading>
                      }
                      body={<Paragraph>Upload a lease for signing, or just to keep your records organized.</Paragraph>}
                      action={
                        <ActionCTA url={actions.leases.url} onClick={handleAddLeaseClick}>
                          Add a lease
                        </ActionCTA>
                      }
                    />
                  )}

                  {actions?.payments.isAllowed && (
                    <Upsell
                      data-testid="success-action-set-up-payments"
                      tone="soft"
                      elevated={false}
                      closeable={false}
                      media={<Icon render={<IconDollarSignCircleFilled />} />}
                      header={
                        <Heading level={3} textStyle="heading-xs">
                          Set up payments
                        </Heading>
                      }
                      body={<Paragraph>Collect rent at no cost to you, and in as few as 2 business days.</Paragraph>}
                      action={
                        <ActionCTA url={actions.payments.url} onClick={handleSetUpPaymentsClick}>
                          Set up payments
                        </ActionCTA>
                      }
                    />
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default PropertyAddedSuccessStep;
